import { useCallback, useEffect, useState } from 'react';

import {
    getLocalStorageItem,
    setLocalStorageItem,
} from '@/shared/lib/local-storage';
import { logError } from '@/shared/lib/logging';

const APP_SIDER_COLLAPSED_STORAGE_KEY = 'app-sider-collapsed';
const isDefaultCollapsed = true;

export function useAppSiderCollapse() {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(isDefaultCollapsed);

    const updateCollapsedState = useCallback((newCollapsedState: boolean) => {
        setIsCollapsed(newCollapsedState);
        setLocalStorageItem(APP_SIDER_COLLAPSED_STORAGE_KEY, newCollapsedState);

        // Dispatch custom event for same-tab updates
        window.dispatchEvent(
            new CustomEvent('appSiderCollapseChanged', {
                detail: { isCollapsed: newCollapsedState },
            }),
        );
    }, []);

    // Load initial value from localStorage
    useEffect(() => {
        const isStoredCollapsed = getLocalStorageItem<boolean>(
            APP_SIDER_COLLAPSED_STORAGE_KEY,
            isDefaultCollapsed,
        );

        setIsCollapsed(isStoredCollapsed);
    }, []);

    // Listen for localStorage changes and custom events
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (
                event.key === APP_SIDER_COLLAPSED_STORAGE_KEY &&
                event.newValue !== null
            ) {
                try {
                    const isNewCollapsed = JSON.parse(
                        event.newValue,
                    ) as boolean;

                    setIsCollapsed(isNewCollapsed);
                } catch (error) {
                    logError(
                        'Failed to parse app sider collapsed state from localStorage',
                        error,
                    );
                }
            }
        };

        const handleCustomEvent = (event: CustomEvent) => {
            if (typeof event.detail?.isCollapsed === 'boolean') {
                setIsCollapsed(event.detail.isCollapsed);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(
            'appSiderCollapseChanged',
            handleCustomEvent as EventListener,
        );

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(
                'appSiderCollapseChanged',
                handleCustomEvent as EventListener,
            );
        };
    }, []);

    return {
        isCollapsed,
        updateCollapsedState,
    };
}
